import ObjectId from 'bson-objectid';
import { FILE_VALIDATION } from '../consts/dragAndDropInputs';

export type ParseUrlInput = {
  url: string;
  isStatic?: boolean;
  size?: number;
};

/** Splices a transformation into a Cloudinary delivery url, after `upload/`. */
export const insertCloudinaryTransformation = (
  url: string,
  transformation: string,
): string => {
  if (!url) return url;

  const uploadIndicator = 'upload/';
  const uploadIndex = url.indexOf(uploadIndicator);
  if (uploadIndex === -1) return url;

  const insertPosition = uploadIndex + uploadIndicator.length;

  return (
    url.slice(0, insertPosition) +
    `${transformation}/` +
    url.slice(insertPosition)
  );
};

export const getScaledCloudinaryUrl = (
  url: string,
  width = 200,
  quality: string | number = 'auto',
): string =>
  insertCloudinaryTransformation(url, `c_scale,w_${width},q_${quality}`);

type ViewType = 'video' | 'image' | 'none';
const imgTypes = [
  'jpg',
  'gif',
  'avif',
  'apng',
  'png',
  'ico',
  'cur',
  'jpeg',
  'jfif',
  'pjpeg',
  'pjp',
  'tif',
  'webp',
  'bmp',
];
const videoTypes = ['mp4', 'mpge', 'avi', 'mov', 'mkv', 'webm', 'ogg', 'ogv'];
export function getMediaType(url: string): ViewType {
  if (url.startsWith('data'))
    return url.startsWith('data:video')
      ? 'video'
      : url.startsWith('data:image')
        ? 'image'
        : 'none';

  const cleanUrl = url.split('?')[0].split('#')[0];

  if (cleanUrl.includes('/video/upload/')) {
    return 'video';
  }
  if (cleanUrl.includes('/image/upload/')) {
    return 'image';
  }

  const arr = cleanUrl.split('.');
  const typ = arr[arr.length - 1].toLowerCase();
  return imgTypes.includes(typ)
    ? 'image'
    : videoTypes.includes(typ)
      ? 'video'
      : 'none';
}

export function formatDistanceToNow(date: Date) {
  const now = new Date();
  const givenDate = new Date(date);
  const diffInMilliseconds = now.getTime() - givenDate.getTime();

  if (isNaN(diffInMilliseconds)) {
    return 'Invalid date';
  }

  const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInMonths / 12);

  if (diffInSeconds < 60) {
    return `${diffInSeconds} seconds ago`;
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minutes ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hours ago`;
  } else if (diffInDays < 30) {
    return `${diffInDays} days ago`;
  } else if (diffInMonths < 12) {
    return `${diffInMonths} months ago`;
  } else {
    return `${diffInYears} years ago`;
  }
}

export const fontColorContrast = (hex: string) => {
  if (!hex) {
    return '#FFFFFFF';
  }
  if (!/^#[0-9A-F]{6}$/i.test(hex)) {
    console.error('Invalid hex color format');
    return '#FFFFFF';
  }

  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);

  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

export const generateObjectId = () => {
  return new ObjectId().toHexString();
};

export interface EmojiValidationResult {
  hasEmoji: boolean;
  cleanedValue: string;
  originalValue: string;
}

export const validateAndCleanEmojis = (
  value: string,
): EmojiValidationResult => {
  const emojiRanges = [
    /[\uD83C-\uDBFF\uDC00-\uDFFF]/g,
    /[\u2700-\u27BF]/g,
    /[\u2600-\u26FF]/g,
  ];

  const hasEmoji = emojiRanges.some((range) => range.test(value));

  if (hasEmoji) {
    const cleanedValue = value.replace(
      /[\uD83C-\uDBFF\uDC00-\uDFFF\u2700-\u27BF\u2600-\u26FF]/g,
      '',
    );
    return { hasEmoji: true, cleanedValue, originalValue: value };
  }

  return { hasEmoji: false, cleanedValue: value, originalValue: value };
};

import { Klydo, KlydoType, SoundMode } from '../api/klydo';

export const hasSound = (soundMode?: SoundMode): boolean => {
  return soundMode === SoundMode.ON || soundMode === SoundMode.FIRST_LOOP;
};

/**
 * Whether a klydo is structurally editable — i.e. not sealed.
 *
 * `type: SealedKlydo` is the terminal lock: finalized and servable, reached via
 * either route (its publication review was approved, or it belongs to an approved
 * collection) and never left again. That single flag covers the collection case
 * too, which is why no separate collection check is needed.
 *
 * The REVIEW LOCK is deliberately not here. "A reviewer is holding it" is a
 * separate question and belongs at the call site, keyed on
 * `public.reviewStatus.state` — see `useKlydoScreen`. Fusing the two here, as a
 * test for the review record's mere existence, locked out the 442 migrated drafts
 * whose record has no `state` at all: they became read-only with no action
 * available anywhere in the UI.
 */
export const isKlydoEditable = (klydo?: Partial<Klydo>): boolean =>
  klydo?.type !== KlydoType.Sealed;

export const createFileValidation = (acceptedFormats: string) => {
  return {
    acceptedMimeTypes:
      acceptedFormats.includes('.png') && acceptedFormats.includes('.jpg')
        ? (['image/png', 'image/jpeg', 'image/jpg'] as const)
        : acceptedFormats.includes('.png')
          ? (['image/png'] as const)
          : (['image/png', 'image/jpeg', 'image/jpg'] as const),
    maxSizeBytes: FILE_VALIDATION.image.maxSizeBytes,
    fileTypeLabel:
      acceptedFormats.includes('.png') && !acceptedFormats.includes('.jpg')
        ? 'PNG'
        : FILE_VALIDATION.image.fileTypeLabel,
  };
};
